import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Grid, GridProps, Box, Heading, Text } from '@chakra-ui/react';
import {
  Fieldset as FieldsetLayout,
  FieldsetProps as FieldsetLayoutProps,
} from 'layouts';
import { useTranslation } from 'react-i18next';

interface FieldsetWithErrorProps {
  heading?: string;
  description?: string;
  columns?: number;
  children?: React.ReactNode;
  errorPath: string;
}

const FieldsetWithError = ({
  heading,
  description,
  columns = 12,
  children,
  errorPath,
  ...props
}: FieldsetWithErrorProps & GridProps & FieldsetLayoutProps): JSX.Element => {
  const { t } = useTranslation();
  const { formState } = useFormContext();
  const error = errorPath
    .split('.')
    .reduce((acc, key) => acc?.[key], formState.errors as any);
  const hasError = !!error;

  return (
    <Grid
      as={FieldsetLayout}
      borderWidth={hasError ? '2px' : '0'}
      borderColor={hasError ? 'red.500' : 'transparent'}
      gap={4}
      templateColumns={`repeat(${columns},1fr)`}
      {...props}
    >
      {(heading || description) && (
        <Box gridColumn={`span ${columns}`}>
          {heading && <Heading size='md'>{heading}</Heading>}
          {description && <Text>{description}</Text>}
        </Box>
      )}
      {children}
      {hasError && (
        <Box gridColumn={`span ${columns}`}>
          <Text color='red.500' fontSize='sm' mt={2}>
            {(error?.message && t(error.message)) ||
              t('thisFieldContainsErrors', 'To pole zawiera błędy')}
          </Text>
        </Box>
      )}
    </Grid>
  );
};

FieldsetWithError.displayName = 'Form.FieldsetWithError';

export { FieldsetWithError };
